// Един ред от хронологията: час · чип с операцията · служител · сума.
// Детайлите (причина, разбивка на плащането, артикули) слизат на втори ред,
// подравнени под чипа.

import { useTranslation } from "react-i18next";

import type { OpEvent } from "../../../../api/billOperations";
import { BillOperationItems } from "./BillOperationItems";
import {
  formatSignedMoney,
  formatTime,
  TONE_CHIP_CLASS,
} from "./billOperationsFormat";
import { describeEvent, isPriceChange } from "./billOperationsEvents";
import { fmtEuro } from "../../../../utils/currency";

type Props = {
  event: OpEvent;
  billName: string;
  billWaiterId: number | null;
  language: string;
  isNarrow: boolean;
};

export const BillOperationEventRow = ({
  event,
  billName,
  billWaiterId,
  language,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();

  const look = describeEvent(event, {
    billName,
    billWaiterId,
    t,
    minuteLabels: {
      hour: t("dataTable.hourShort"),
      minute: t("dataTable.minuteShort"),
    },
  });

  const userName = event.user
    ? event.user.names || `#${event.user.id}`
    : null;
  const items = event.items ?? [];
  const hasDetails = look.notes.length > 0 || items.length > 0;
  const priceChangePercent =
    (event.type === "discount_apply" || event.type === "discount_remove") &&
    isPriceChange(event) &&
    event.discount_percent != null
      ? Number(event.discount_percent)
      : undefined;

  return (
    <li
      className={`border-b border-shade-1 last:border-b-0 ${
        isNarrow ? "px-4 py-2.5" : "px-6 py-3"
      }`}
    >
      <div className={`flex items-baseline ${isNarrow ? "gap-2.5" : "gap-3"}`}>
        <span
          className={`shrink-0 font-pos-mono text-[13px] font-semibold tabular-nums text-muted ${
            isNarrow ? "w-[36px]" : "w-[42px]"
          }`}
        >
          {formatTime(event.at)}
        </span>

        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={`inline-flex items-center rounded-full px-2 py-[3px] text-[11.5px] font-semibold leading-[normal] ${
              TONE_CHIP_CLASS[look.tone]
            }`}
          >
            {look.label}
          </span>
          {userName ? (
            <span className="truncate text-[13px] font-medium text-soft">
              {userName}
            </span>
          ) : null}
          {event.comment ? (
            // Коментарът на поръчката — курсив в кавички, до служителя. Не се
            // реже: дългият слиза на свой ред (контейнерът е flex-wrap).
            <span className="min-w-0 break-words text-[13px] italic text-soft">
              {`– „${event.comment}“`}
            </span>
          ) : null}
        </span>

        {look.amount !== null ? (
          <span
            className={`shrink-0 text-right font-pos-mono text-[13.5px] font-bold tabular-nums ${
              !look.amountUnsigned && look.amount < 0
                ? "text-danger-ink"
                : "text-ink"
            }`}
          >
            {look.amountUnsigned
              ? fmtEuro(look.amount)
              : formatSignedMoney(look.amount)}
          </span>
        ) : null}
      </div>

      {hasDetails ? (
        <div
          className={`mt-1.5 flex flex-col gap-1 ${
            isNarrow ? "pl-[46px]" : "pl-[54px]"
          }`}
        >
          {look.notes.map((note) => (
            <p key={note} className="text-[12.5px] leading-[1.4] text-soft">
              {note}
            </p>
          ))}
          {items.length > 0 ? (
            <BillOperationItems
              items={items}
              language={language}
              negative={event.type === "cancel"}
              priceChangePercent={priceChangePercent}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  );
};
